// src/services/api.ts
import axios from 'axios';

const API_BASE_URL = 'https://palmgarden-app-api-prod.yaoyy13.workers.dev/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'super-admin' | 'global-read';
  display_name: string;
}

export interface Plant {
  id: number;
  species_uuid: string;
  slug: string;
  primary_scientific_name: string;
  scientific_name_author: string | null;
  scientific_name_year: number | null;
  family: string;
  genus: string;
  species: string;
  description: string;
  detailed_description: string;
  size_category: string;
  height_min: number | null;
  height_max: number | null;
  width_min: number | null;
  width_max: number | null;
  status: string;
  iucn_status: string;
  uploaded_by: number;
  reviewed_by: number | null;
  featured: number;
  view_count: number;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface BotanicalCharacteristic {
  id: number;
  category: string;
  value: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

export interface BotanicalContentPost {
  id: number;
  plant_id: number;
  author_id: number;
  content_type: string;
  title: string;
  content: string;
  section: string;
  subsection: string;
  sort_order: number;
  status: string;
  created_at: string;
  updated_at: string;
}

// Auth API
export const authAPI = {
  login: async (identifier: string, password: string) => {
    const response = await api.post('/auth/login', { identifier, password });
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  }
};

// Plants API
export const plantsAPI = {
  getPlants: async (params?: {
    search?: string;
    category?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/plants', { params });
    return response.data;
  },
  
  getPlant: async (id: number) => {
    const response = await api.get(`/plants/${id}`);
    return response.data;
  },
  
  createPlant: async (plantData: Partial<Plant>) => {
    const response = await api.post('/admin/plants', plantData);
    return response.data;
  },
  
  updatePlant: async (id: number, plantData: Partial<Plant>) => {
    const response = await api.put(`/admin/plants/${id}`, plantData);
    return response.data;
  },
  
  deletePlant: async (id: number) => {
    const response = await api.delete(`/admin/plants/${id}`);
    return response.data;
  },
  
  searchPlants: async (query: string) => {
    const response = await api.get('/plants/search', { params: { q: query } });
    return response.data;
  },
  
  getPlantSynonyms: async (plantId: number) => {
    const response = await api.get(`/plants/${plantId}/synonyms`);
    return response.data;
  },
  
  getPlantCommonNames: async (plantId: number) => {
    const response = await api.get(`/plants/${plantId}/common-names`);
    return response.data;
  }
};

// Botanical API
export const botanicalAPI = {
  getCharacteristics: async (category?: string) => {
    const response = await api.get('/botanical/characteristics', {
      params: category ? { category } : {}
    });
    return response.data;
  },
  
  getPlantBotanicalContent: async (plantId: number) => {
    const response = await api.get(`/plants/${plantId}/botanical-content`);
    return response.data;
  },
  
  createBotanicalContent: async (plantId: number, contentData: Partial<BotanicalContentPost>) => {
    const response = await api.post(`/admin/plants/${plantId}/botanical-content`, contentData);
    return response.data;
  },
  
  updateBotanicalContent: async (contentId: number, contentData: Partial<BotanicalContentPost>) => {
    const response = await api.put(`/admin/botanical-content/${contentId}`, contentData);
    return response.data;
  },
  
  deleteBotanicalContent: async (contentId: number) => {
    const response = await api.delete(`/admin/botanical-content/${contentId}`);
    return response.data;
  }
};

// Images API
export const imagesAPI = {
  getPlantImages: async (plantId: number) => {
    const response = await api.get(`/plants/${plantId}/images`);
    return response.data;
  },
  
  estimateUploadTime: async (fileSize: number, bandwidthMbps: number = 100) => {
    const response = await api.post('/upload/estimate', {
      file_size: fileSize,
      bandwidth_mbps: bandwidthMbps
    });
    return response.data;
  }
};

// PDF Export API
export const pdfAPI = {
  exportPlantPDF: async (plantId: number, options?: any) => {
    const response = await api.post(`/admin/plants/${plantId}/export/pdf`, options, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportPlantBook: async (options?: any) => {
    const response = await api.post('/admin/plants/export/book', options, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// OSM API
export const osmAPI = {
  searchPlaces: async (query: string) => {
    const response = await api.get('/osm/search', { params: { q: query } });
    return response.data;
  },
  
  reverseGeocode: async (lat: number, lon: number) => {
    const response = await api.get('/osm/reverse', { params: { lat, lon } });
    return response.data;
  }
};

export default api;
